import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery
} from "@mui/material";
import SearchField from "../common/SearchField";
import { BIBlue, lightenHexColor } from "../../assets/helpers";
import FilterListIcon from "@mui/icons-material/FilterList";
import CancelIcon from "@mui/icons-material/Cancel";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import {
  SEARCH_PROJECTS_NEW
} from "../../constant/Apipath";
import Pageheader from "../common/Pageheader";
import Emptydata from "../common/Emptydata";
import { English } from "../../constant/EnglishFallback";
import ProjectsFilterModal from "./ProjectsFilterModal";
import Loader from "../../common/Loader";
import UserUsages from "../common/UserUsages";
import UseDebounce from "../common/UseDebounce";
import { CompaniesSkeleton } from "../skeleton/SkeletonView";
import AdContainer from "../common/AdContainer";

const PAGE_SIZE = 20;

const formatText = (text = "") =>
  text.replace(/\w*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());

const Projects = () => {
  const { loginData, multiLanguageData } = useSelector((state) => state?.main);
  const LanguageData = multiLanguageData?.Projects || {};

  const {
    TEXT_PROJECTS = English?.Projects?.TEXT_PROJECTS || "Projects",
    TEXT_SEARCH_PROJECTS = "Search Projects",
    TEXT_RESULTS = English?.Companies?.TEXT_RESULTS || "Results",
    TEXT_NO_PROJECTS_EXIST = "No projects available in our records at the moment.",
    TEXT_FILTER = "Filter"
  } = LanguageData;

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // State
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [nrOfRecords, setNrOfRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState(location?.state?.searchTerm || "");
  const [pageNr, setPageNr] = useState(1);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Filters
  const [selectedProjectType, setSelectedProjectType] = useState(
    location?.state?.selectedProjectType || null
  );
  const [selectedCompany, setSelectedCompany] = useState(
    location?.state?.selectedCompany || null
  );
  const [address, setAddress] = useState(location?.state?.searchLocation || "");
  const [addressDetails, setAddressDetails] = useState(null);

  const abortControllerRef = useRef(null);
  const isFirstMount = useRef(true);

  const activeFilterCount =
    (selectedProjectType ? 1 : 0) +
    (selectedCompany ? 1 : 0) +
    (address?.trim() ? 1 : 0);

  // Track component usage
  useEffect(() => {
    UserUsages("Projects", loginData);
  }, []);

  const debouncedSearchTerm = UseDebounce(searchTerm, 400);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      getData("", 1, selectedProjectType, selectedCompany, address, addressDetails);
      return;
    }
    setPageNr(1);
    setData([]);
    setNrOfRecords(0);
    getData(debouncedSearchTerm, 1, selectedProjectType, selectedCompany, address, addressDetails);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const getData = async (
    currentSearchTerm = searchTerm,
    pageNo = 1,
    typeFilter = selectedProjectType,
    companyFilter = selectedCompany,
    addressFilter = address,
    addressDetailsFilter = addressDetails
  ) => {
    try {
      if (pageNo === 1) {
        setIsLoading(true);
      }
      setAllDataLoaded(true);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const cleanAddress = (addressFilter || "").trim();
      const searchItem = {};

      if (typeFilter?.id || typeFilter?.value) {
        const typeId = Number(typeFilter.id ?? typeFilter.value);
        searchItem.ProjectTypeId = !isNaN(typeId) ? typeId : (typeFilter.id ?? typeFilter.value);
      }

      if (companyFilter?.id || companyFilter?.value) {
        const compId = Number(companyFilter.id ?? companyFilter.value);
        searchItem.CompanyId = !isNaN(compId) ? compId : (companyFilter.id ?? companyFilter.value);
      }

      if (cleanAddress) {
        searchItem.Address = cleanAddress;
      }

      if (addressDetailsFilter?.city) {
        searchItem.City = addressDetailsFilter.city.trim();
      }

      if (addressDetailsFilter?.province || addressDetailsFilter?.province_long) {
        const prov = (addressDetailsFilter.province_long || addressDetailsFilter.province).trim();
        searchItem.Province = prov;
      }

      if (addressDetailsFilter?.country) {
        searchItem.Country = addressDetailsFilter.country.trim();
      }

      if (addressDetailsFilter?.postalCode) {
        searchItem.PostalCode = addressDetailsFilter.postalCode.trim();
      }

      if (addressDetailsFilter?.latitude && addressDetailsFilter?.longitude) {
        searchItem.Latitude = addressDetailsFilter.latitude;
        searchItem.Longitude = addressDetailsFilter.longitude;
      }

      const hasSearchItem = Object.keys(searchItem).length > 0;
      const searchList = hasSearchItem ? [searchItem] : [{}];

      const payload = {
        PageNr: pageNo,
        NrOfRecPerPage: PAGE_SIZE,
        FullSearch: currentSearchTerm?.trim() || "",
        UserId: loginData?.userInformation?.UserId ?? loginData?.userInformation?.userId ?? 1,
        FetchAllowedRecordsOnly: true,
        IncludeRecordNr: true,
        SearchList: searchList,
        SortList: [
          {
            FieldName: "Id",
            Direction: "DESC"
          }
        ]
      };

      const res = await Postrequestcall(
        SEARCH_PROJECTS_NEW,
        payload,
        loginData?.accessToken
      );

      const resData = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.dataList)
        ? res.data.dataList
        : Array.isArray(res?.data)
        ? res.data
        : [];

      const totalRecords = Number(
        res?.data?.nrOfRecords ??
        res?.data?.RecordNr ??
        res?.data?.totalRecords ??
        res?.nrOfRecords ??
        res?.RecordNr ??
        res?.totalRecords ??
        resData.length ??
        0
      );

      if (pageNo === 1) {
        setData(resData);
      } else {
        setData((prevData) => {
          const seen = new Set(prevData.map((p) => p.id || p.projectId || p.Id || p.ProjectId));
          const fresh = resData.filter((p) => !seen.has(p.id || p.projectId || p.Id || p.ProjectId));
          return [...prevData, ...fresh];
        });
      }
      setNrOfRecords(totalRecords);
      setAllDataLoaded(false);
      setIsLoading(false);
    } catch (error) {
      if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
        console.error("getData-> Error When fetching Projects: ", error);
      }
      setAllDataLoaded(false);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      setAllDataLoaded(false);
    }
  };

  const handleApplyFilters = (filters) => {
    setSelectedProjectType(filters.selectedProjectType);
    setSelectedCompany(filters.selectedCompany);
    setAddress(filters.address);
    setAddressDetails(filters.addressDetails);
    setPageNr(1);
    setData([]);
    setNrOfRecords(0);
    getData(
      searchTerm,
      1,
      filters.selectedProjectType,
      filters.selectedCompany,
      filters.address,
      filters.addressDetails
    );
  };

  const handleClearAllFilters = () => {
    setSelectedProjectType(null);
    setSelectedCompany(null);
    setAddress("");
    setAddressDetails(null);
    setPageNr(1);
    setData([]);
    setNrOfRecords(0);
    getData(searchTerm, 1, null, null, "", null);
  };

  const handleRemoveFilter = (filterKey) => {
    let nextType = selectedProjectType;
    let nextCompany = selectedCompany;
    let nextAddress = address;
    let nextAddressDetails = addressDetails;

    if (filterKey === "projectType") {
      nextType = null;
      setSelectedProjectType(null);
    } else if (filterKey === "company") {
      nextCompany = null;
      setSelectedCompany(null);
    } else if (filterKey === "address") {
      nextAddress = "";
      nextAddressDetails = null;
      setAddress("");
      setAddressDetails(null);
    }

    setPageNr(1);
    setData([]);
    setNrOfRecords(0);
    getData(
      searchTerm,
      1,
      nextType,
      nextCompany,
      nextAddress,
      nextAddressDetails
    );
  };

  const handleLoadMore = async () => {
    if (data.length < nrOfRecords) {
      const nextPage = pageNr + 1;
      await getData(
        searchTerm,
        nextPage,
        selectedProjectType,
        selectedCompany,
        address,
        addressDetails
      );
      setPageNr(nextPage);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const onProjectClick = (item) => {
    const projectId = item?.id || item?.projectId || item?.Id || item?.ProjectId;
    if (projectId) {
      navigate(`/project/${projectId}`, { state: { projectData: item } });
    }
  };

  const getAddress = (item) => {
    return [
      item.address || item.Address,
      (item.city || item.City) ? formatText(item.city || item.City) : "",
      item.province || item.Province,
      (item.country || item.Country) ? formatText(item.country || item.Country) : ""
    ]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <Box className="main-content-Box" sx={{ p: isSmallScreen ? 2 : 3, pt: 2 }}>
      <Pageheader headerName={TEXT_PROJECTS} />
      <Card className="main-content-card">
        {/* Header & Search Bar - Identical to Companies.jsx */}
        <Grid
          sx={{
            pb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
          container
          spacing={1}
        >
          <Grid size={{ xs: 12, sm: 8, md: 6, lg: 5 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ width: "100%", alignItems: "center" }}
            >
              <Box sx={{ flex: 1 }}>
                <SearchField
                  size={"small"}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onSubmit={() => {
                    setPageNr(1);
                    setData([]);
                    setNrOfRecords(0);
                    getData(
                      searchTerm,
                      1,
                      selectedProjectType,
                      selectedCompany,
                      address,
                      addressDetails
                    );
                  }}
                  onClear={() => {
                    setSearchTerm("");
                    setPageNr(1);
                    setData([]);
                    setNrOfRecords(0);
                    getData(
                      "",
                      1,
                      selectedProjectType,
                      selectedCompany,
                      address,
                      addressDetails
                    );
                  }}
                  searchIconOutside
                  placeholder={TEXT_SEARCH_PROJECTS}
                />
              </Box>
              <Button
                variant={activeFilterCount > 0 ? "contained" : "outlined"}
                startIcon={<FilterListIcon />}
                endIcon={
                  activeFilterCount > 0 ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearAllFilters();
                      }}
                      sx={{ p: 0.2, color: "inherit", ml: 0.2 }}
                    >
                      <CancelIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : null
                }
                onClick={() => setShowFilterModal(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  borderRadius: 2,
                  height: 38,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  ...(activeFilterCount > 0
                    ? {
                        bgcolor: BIBlue,
                        color: "#fff",
                        "&:hover": { bgcolor: BIBlue }
                      }
                    : {
                        borderColor: "#d0d0d0",
                        color: "text.primary"
                      })
                }}
              >
                {activeFilterCount > 0
                  ? `Filters (${activeFilterCount})`
                  : TEXT_FILTER}
              </Button>
            </Stack>
          </Grid>
          <Grid
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" }
            }}
            size={{ xs: 12, sm: 4, md: 3, lg: 2 }}
          >
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
              {nrOfRecords} {TEXT_RESULTS}
            </Typography>
          </Grid>
        </Grid>

        {/* Active Filter Chips Row - Identical to Companies.jsx */}
        {activeFilterCount > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mb: 1.5, gap: 1 }}
          >
            {selectedProjectType && (
              <Chip
                icon={<CategoryIcon sx={{ fontSize: "16px !important", color: `${BIBlue} !important` }} />}
                label={selectedProjectType.name || selectedProjectType.label}
                onDelete={() => handleRemoveFilter("projectType")}
                size="small"
                sx={{
                  bgcolor: lightenHexColor(BIBlue, 92),
                  color: BIBlue,
                  fontWeight: 600,
                  borderColor: lightenHexColor(BIBlue, 70),
                  "& .MuiChip-icon": { color: BIBlue },
                  "& .MuiChip-deleteIcon": { color: BIBlue }
                }}
                variant="outlined"
              />
            )}
            {selectedCompany && (
              <Chip
                icon={<BusinessIcon sx={{ fontSize: "16px !important", color: `${BIBlue} !important` }} />}
                label={(selectedCompany.name || selectedCompany.companyName || "").trim()}
                onDelete={() => handleRemoveFilter("company")}
                size="small"
                sx={{
                  bgcolor: lightenHexColor(BIBlue, 92),
                  color: BIBlue,
                  fontWeight: 600,
                  borderColor: lightenHexColor(BIBlue, 70),
                  "& .MuiChip-icon": { color: BIBlue },
                  "& .MuiChip-deleteIcon": { color: BIBlue }
                }}
                variant="outlined"
              />
            )}
            {address?.trim() && (
              <Chip
                icon={<LocationOnIcon sx={{ fontSize: "16px !important", color: `${BIBlue} !important` }} />}
                label={address}
                onDelete={() => handleRemoveFilter("address")}
                size="small"
                sx={{
                  bgcolor: lightenHexColor(BIBlue, 92),
                  color: BIBlue,
                  fontWeight: 600,
                  borderColor: lightenHexColor(BIBlue, 70),
                  "& .MuiChip-icon": { color: BIBlue },
                  "& .MuiChip-deleteIcon": { color: BIBlue }
                }}
                variant="outlined"
              />
            )}
            <Button
              size="small"
              variant="text"
              onClick={handleClearAllFilters}
              sx={{
                textTransform: "none",
                fontSize: "0.78rem",
                color: "text.secondary",
                fontWeight: 600,
                minWidth: "auto",
                p: 0.5
              }}
            >
              Clear all
            </Button>
          </Stack>
        )}

        {/* List Section - Identical Structure & Classes to Companies.jsx */}
        {isLoading ? (
          <>
            <CompaniesSkeleton />
          </>
        ) : (
          <>
            <Box className="main-content-card-list" id="scrollablediv_projects">
              {data.length !== 0 ? (
                <InfiniteScroll
                  dataLength={data?.length}
                  next={handleLoadMore}
                  hasMore={data.length < nrOfRecords}
                  loader={
                    !allDataLoaded ? null : (
                      <h4 style={{ display: "flex", justifyContent: "center" }}>
                        <Loader />
                      </h4>
                    )
                  }
                  scrollableTarget="scrollablediv_projects"
                >
                  <List disablePadding>
                    <Stack sx={{ gap: 1 }}>
                      {data.map((item, index) => {
                        const title =
                          item.projectName ||
                          item.name ||
                          item.ProjectName ||
                          item.Name ||
                          "Unnamed Project";
                        const imgUri =
                          item.thumbUrl ||
                          item.pictureUrl ||
                          item.ThumbUrl ||
                          item.PictureUrl ||
                          (item.pictureList && item.pictureList[0]?.thumbUrl) ||
                          (item.PictureList && item.PictureList[0]?.ThumbUrl);
                        const fullAddr = getAddress(item);
                        const isActive =
                          item.isActive !== undefined ? item.isActive : item.IsActive;
                        const projectTypeName =
                          item.projectTypeName || item.ProjectTypeName;
                        const companyName =
                          item.companyName || item.CompanyName;

                        return (
                          <Card
                            key={item.id || item.projectId || item.Id || item.ProjectId || index}
                            onClick={() => onProjectClick(item)}
                            className="pagelist-content"
                            sx={{ cursor: "pointer" }}
                          >
                            <ListItem
                              disablePadding
                              secondaryAction={
                                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                  {isActive && (
                                    <Box
                                      component="span"
                                      sx={{
                                        fontSize: "0.75rem",
                                        bgcolor: "#ECFDF5",
                                        color: "#059669",
                                        border: "1px solid #A7F3D0",
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        fontWeight: 600
                                      }}
                                    >
                                      Active
                                    </Box>
                                  )}
                                  <IconButton
                                    edge="end"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onProjectClick(item);
                                    }}
                                  >
                                    <ChevronRightIcon />
                                  </IconButton>
                                </Stack>
                              }
                            >
                              <ListItemButton
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  pr: { xs: 8, sm: 12 }
                                }}
                              >
                                <Avatar
                                  src={imgUri}
                                  variant="rounded"
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    mr: 1.5,
                                    bgcolor: lightenHexColor(BIBlue, 92)
                                  }}
                                >
                                  <BusinessIcon sx={{ fontSize: 24, color: BIBlue }} />
                                </Avatar>

                                <ListItemText
                                  primary={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        minWidth: 0
                                      }}
                                    >
                                      <Typography
                                        component="span"
                                        variant="body1"
                                        noWrap
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {title}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    <Stack spacing={0.3} sx={{ mt: 0.25 }}>
                                      {fullAddr ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          noWrap
                                        >
                                          {fullAddr}
                                        </Typography>
                                      ) : null}
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        {projectTypeName && (
                                          <Chip
                                            label={projectTypeName}
                                            size="small"
                                            sx={{
                                              height: 20,
                                              fontSize: "0.72rem",
                                              bgcolor: lightenHexColor(BIBlue, 94),
                                              color: BIBlue,
                                              fontWeight: 600
                                            }}
                                          />
                                        )}
                                        {companyName && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                            sx={{ fontWeight: 500 }}
                                          >
                                            {companyName}
                                          </Typography>
                                        )}
                                      </Stack>
                                    </Stack>
                                  }
                                  sx={{ flex: 1, minWidth: 0 }}
                                />
                              </ListItemButton>
                            </ListItem>
                          </Card>
                        );
                      })}
                    </Stack>
                  </List>
                </InfiniteScroll>
              ) : (
                <Box className="empty-data-card">
                  <Emptydata
                    icon={<BusinessCenterIcon className="emptydata-icon" />}
                    firstText={TEXT_NO_PROJECTS_EXIST}
                  />
                </Box>
              )}
            </Box>
          </>
        )}
        <AdContainer />
      </Card>

      {/* Filter Modal */}
      {showFilterModal && (
        <ProjectsFilterModal
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          initialFilters={{
            selectedProjectType,
            selectedCompany,
            address,
            addressDetails
          }}
          onApplyFilters={handleApplyFilters}
        />
      )}
    </Box>
  );
};

export default Projects;
